<template>
  <Teleport v-if="mounted && !publicLayout" to="#app-status-region">
    <div v-if="!online" role="status" class="app-connection-status border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      <strong>Sin conexión.</strong> Conéctate a internet para consultar o guardar. Lo que estás escribiendo permanece en pantalla.
    </div>
    <div v-else-if="availableUpdate" role="status" class="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900">
      <span>{{ updateReady ? 'Nueva versión lista. Guarda tu trabajo antes de actualizar.' : 'Preparando la nueva versión. Puedes seguir trabajando.' }}</span>
      <button type="button" class="pwa-update-button inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70"
        :disabled="updateInProgress || !updateReady" @click="startUpdate">
        <svg class="pwa-update-icon h-5 w-5" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 7a8 8 0 1 0 1 7M20 3v5h-5" />
        </svg>
        {{ updateInProgress ? 'Actualizando…' : updateReady ? 'Ver actualización' : 'Preparando actualización…' }}
      </button>
    </div>
  </Teleport>
  <AppDialog v-if="showInstructions" title="Instalar J&R ERP SOFT" :busy="installing" @close="showInstructions = false">
    <div class="mb-5 flex items-center gap-3">
      <img src="/img/icons/apple-touch-icon-180x180.png" alt="" class="h-14 w-14 shrink-0 rounded-xl" />
      <p class="text-sm text-slate-600">Abre el sistema desde tu pantalla de inicio, en su propia ventana.</p>
    </div>
    <p v-if="!secure" class="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Abre la dirección segura del sistema (HTTPS) para instalar la aplicación.</p>
    <template v-else>
      <ol v-if="os === 'ios'" class="list-decimal space-y-4 pl-5 text-slate-700">
        <li>Abre el menú <strong>Compartir</strong> del navegador.</li>
        <li>Selecciona <strong>Añadir a pantalla de inicio</strong>.</li>
        <li>Si aparece <strong>Abrir como app web</strong>, déjalo activado y toca <strong>Añadir</strong>.</li>
      </ol>
      <p v-else-if="deferredPrompt" class="text-slate-700">Toca <strong>Instalar aplicación</strong> y confirma la instalación en el navegador.</p>
      <ol v-else class="list-decimal space-y-4 pl-5 text-slate-700">
        <li>Abre el menú del navegador.</li>
        <li>Selecciona <strong>Instalar aplicación</strong> o <strong>Añadir a pantalla de inicio</strong> y confirma.</li>
      </ol>
      <p v-if="os === 'ios'" class="mt-4 text-sm text-slate-500">Si no encuentras la opción, abre esta misma dirección en Safari.</p>
    </template>
    <p class="mt-5 text-sm text-slate-500">Necesitas conexión a internet para trabajar con tus datos.</p>
    <p v-if="installError" role="alert" class="mt-3 text-sm text-red-700">{{ installError }}</p>
    <template #actions>
      <button type="button" class="rounded-lg border px-4 py-2 text-slate-700" :disabled="installing" @click="showInstructions = false">Cerrar</button>
      <button v-if="deferredPrompt && secure && os !== 'ios'" type="button" class="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
        :disabled="installing || !online" @click="install">{{ installing ? 'Instalando…' : 'Instalar aplicación' }}</button>
    </template>
  </AppDialog>
</template>
<script>
import { onMounted, onBeforeUnmount, ref, shallowRef } from 'vue';
import AppDialog from './AppDialog.vue';
import PWADetector from '@/utils/pwa/pwaDetector';
import { availableUpdate, updateInProgress, updateReady, applyAvailableUpdate } from '@/utils/pwa/updates';

export default {
  name: 'InstallPWA',
  components: { AppDialog },
  props: { publicLayout: Boolean },
  emits: ['installed'],
  setup(props, { emit }) {
    const mounted = ref(false), online = ref(navigator.onLine !== false), showInstructions = ref(false);
    const deferredPrompt = shallowRef(null), installing = ref(false), installError = ref('');
    const startUpdate = () => { if (updateReady.value) applyAvailableUpdate(); };
    const os = PWADetector.getOS(), secure = window.isSecureContext;
    const connectivity = () => { online.value = navigator.onLine !== false; };
    const promptReady = event => { event.preventDefault(); deferredPrompt.value = event; };
    const installed = () => { deferredPrompt.value = null; showInstructions.value = false; emit('installed'); };
    const open = () => { if (!PWADetector.isPWAMode()) { installError.value = ''; showInstructions.value = true; } };
    const install = async () => {
      if (!deferredPrompt.value || installing.value) return;
      installing.value = true; installError.value = '';
      const prompt = deferredPrompt.value; deferredPrompt.value = null;
      try {
        await prompt.prompt();
        const choice = await prompt.userChoice;
        if (choice.outcome === 'accepted') showInstructions.value = false;
      } catch (error) { installError.value = 'No se pudo abrir la instalación. Puedes usar el menú del navegador.'; }
      finally { installing.value = false; }
    };
    onMounted(() => {
      mounted.value = true;
      window.addEventListener('online', connectivity); window.addEventListener('offline', connectivity);
      window.addEventListener('beforeinstallprompt', promptReady); window.addEventListener('appinstalled', installed);
      window.addEventListener('pwa:install-request', open);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('online', connectivity); window.removeEventListener('offline', connectivity);
      window.removeEventListener('beforeinstallprompt', promptReady); window.removeEventListener('appinstalled', installed);
      window.removeEventListener('pwa:install-request', open);
    });
    return { mounted, online, os, secure, showInstructions, deferredPrompt, installing, installError,
      install, availableUpdate, updateInProgress, updateReady, startUpdate };
  },
};
</script>
<style scoped>
.pwa-update-button { animation: pwa-update-glow 2.4s ease-in-out infinite; }
.pwa-update-icon { animation: pwa-update-turn 3s ease-in-out infinite; }
@keyframes pwa-update-glow { 50% { box-shadow: 0 0 0 5px rgb(37 99 235 / 15%); } }
@keyframes pwa-update-turn { 0%, 65% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .pwa-update-button, .pwa-update-icon { animation: none; }
}
</style>
